import { Component, OnInit, Input, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApiService } from '@core/services/api.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { HeaderService } from '@core/services/header.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { PaginationComponent } from '@shared/components/pagination/pagination.component';
import { CustomSelectComponent } from '@shared/components/custom-select/custom-select.component';
import { MENU_REGISTRY } from '@core/models/role-config.model';

@Component({
  selector: 'app-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatProgressSpinnerModule, PaginationComponent, CustomSelectComponent]
})
export class RolesComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() mode: 'both' | 'roles' | 'users' = 'both';

  activeTab: 'roles' | 'users' = 'users';

  loading = true;
  submitting = false;
  roles: any[] = [];

  showDialog = false;
  isEdit = false;
  drawerMode: 'view' | 'edit' = 'view';
  roleForm: any = this.emptyForm();

  selectedRole: any = null;

  users: any[] = [];
  usersLoading = false;
  userSearch = '';
  roleFilter = '';
  changingRoleFor: string | null = null;
  usersPage = 1;
  usersPageSize = 10;

  readonly menuItems = MENU_REGISTRY;

  tabsByPage: { [pageKey: string]: { key: string; label: string }[] } = {};

  readonly sectionDefs: { key: string; label: string; short: string; icon: string; keys: string[] }[] = [
    { key: 'home',      label: 'Home',              short: 'Home',     icon: 'home',                 keys: ['dashboard', 'my_team'] },
    { key: 'time',      label: 'Time & Attendance', short: 'Time',     icon: 'schedule',              keys: ['attendance', 'roster', 'timesheet', 'leave', 'holidays'] },
    { key: 'workforce', label: 'Workforce',         short: 'People',   icon: 'groups',                keys: ['employees', 'departments', 'onboarding', 'offboarding', 'performance', 'appraisal', 'asset_management'] },
    { key: 'payroll',   label: 'Payroll & Billing', short: 'Payroll',  icon: 'payments',              keys: ['payroll', 'clients'] },
    { key: 'insights',  label: 'Insights',          short: 'Insights', icon: 'insights',              keys: ['reports'] },
    { key: 'admin',     label: 'Administration',    short: 'Admin',    icon: 'admin_panel_settings',  keys: ['master_data', 'settings', 'policies', 'migration', 'activity_log', 'geo_zones'] },
    { key: 'account',   label: 'Account',           short: 'Account',  icon: 'account_circle',        keys: ['my_payslips', 'change_requests', 'profile', 'announcements'] },
    { key: 'other',     label: 'Other',             short: 'Other',    icon: 'apps',                  keys: ['overtime', 'teams', 'people', 'org_chart', 'roles', 'email_templates'] },
  ];

  activeSectionKey: string | null = null;

  selectSection(key: string): void {
    this.activeSectionKey = key;
  }

  pagesForSection(sectionKey: string): typeof MENU_REGISTRY {
    const def = this.sectionDefs.find(s => s.key === sectionKey);
    if (!def) return [];
    return def.keys
      .map(k => this.menuItems.find(m => m.key === k))
      .filter((m): m is typeof MENU_REGISTRY[number] => !!m);
  }

  sectionGrantedCount(sectionKey: string): number {
    return this.pagesForSection(sectionKey).filter(p => this.isMenuSelected(p.key)).length;
  }

  sectionGrantedCountView(sectionKey: string): number {
    const granted = this.getSelectedMenuKeys();
    return this.pagesForSection(sectionKey).filter(p => granted.includes(p.key)).length;
  }

  private seedActiveSection(grantedKeys: string[]): void {
    const withGrant = this.sectionDefs.find(s => s.keys.some(k => grantedKeys.includes(k)));
    this.activeSectionKey = (withGrant ?? this.sectionDefs[0]).key;
  }

  getSectionLabel(sectionKey: string): string {
    return this.sectionDefs.find(s => s.key === sectionKey)?.label ?? sectionKey;
  }

  private loadTabRegistry(): void {
    this.apiService.get('/roles/registry').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const tabs = (res?.tabs ?? res?.data?.tabs ?? []) as any[];
        const grouped: { [k: string]: { key: string; label: string }[] } = {};
        for (const t of tabs) {
          (grouped[t.pageKey] ??= []).push({ key: t.key, label: t.label });
        }
        this.tabsByPage = grouped;
      },
      error: () => { this.tabsByPage = {}; },
    });
  }

  tabsForMenu(pageKey: string): { key: string; label: string }[] {
    if (!this.isMenuSelected(pageKey)) return [];
    return this.tabsByPage[pageKey] ?? [];
  }

  allTabsForMenu(pageKey: string): { key: string; label: string }[] {
    return this.tabsByPage[pageKey] ?? [];
  }

  selectedTabCountForMenu(pageKey: string): number {
    return this.allTabsForMenu(pageKey).filter(t => this.isTabSelected(t.key)).length;
  }

  expandedMenus = new Set<string>();

  isExpanded(key: string): boolean {
    return this.expandedMenus.has(key);
  }

  toggleExpand(key: string, event?: Event): void {
    event?.stopPropagation();
    if (this.expandedMenus.has(key)) this.expandedMenus.delete(key);
    else this.expandedMenus.add(key);
  }

  private seedExpanded(grantedKeys: string[]): void {
    this.expandedMenus = new Set(grantedKeys.filter(k => this.allTabsForMenu(k).length > 0));
  }

  isTabSelected(tabKey: string): boolean {
    return this.roleForm.tabKeys?.includes(tabKey) ?? false;
  }

  toggleTab(tabKey: string): void {
    const arr = this.roleForm.tabKeys ?? (this.roleForm.tabKeys = []);
    const idx = arr.indexOf(tabKey);
    if (idx > -1) arr.splice(idx, 1); else arr.push(tabKey);
  }

  readonly modules = [
    'employees', 'attendance', 'leave', 'departments', 'teams',
    'payroll', 'performance', 'onboarding', 'offboarding', 'asset_management',
    'announcements', 'clients', 'reports', 'settings', 'roles', 'holidays',
    'overtime', 'timesheet', 'roster', 'master_data', 'people', 'geo_zones',
    'policies', 'activity_log', 'change_requests'
  ];

  readonly ops = ['create', 'read', 'update', 'delete'];

  readonly presets: Record<string, string> = {
    super_admin: 'full',
    admin:       'full',
    hr_admin:    'hr',
    hr:          'hr',
    manager:     'manager',
    employee:    'employee',
  };

  private getOpDefaults(presetKey: string): Record<string, { create: boolean; read: boolean; update: boolean; delete: boolean }> {
    const mods = this.modules;
    const all  = (m: string) => ({ create: true,  read: true,  update: true,  delete: true  });
    const none = (m: string) => ({ create: false, read: false, update: false, delete: false });
    switch (presetKey) {
      case 'full':
        return Object.fromEntries(mods.map(m => [m, all(m)]));
      case 'hr':
        return Object.fromEntries(mods.map(m => [m, {
          create: ['employees','attendance','leave','onboarding','offboarding','announcements','asset_management','payroll'].includes(m),
          read:   true,
          update: ['employees','attendance','leave','onboarding','offboarding','announcements','asset_management','payroll','departments','teams','holidays'].includes(m),
          delete: ['attendance','leave','announcements'].includes(m),
        }]));
      case 'manager':
        return Object.fromEntries(mods.map(m => [m, {
          create: ['attendance','leave','overtime','timesheet'].includes(m),
          read:   ['employees','attendance','leave','departments','teams','performance','reports','holidays',
                   'overtime','timesheet','roster','policies','change_requests'].includes(m),
          update: ['attendance','leave','overtime','timesheet','change_requests'].includes(m),
          delete: false,
        }]));
      case 'employee':
        return Object.fromEntries(mods.map(m => [m, {
          create: ['attendance','leave','overtime','timesheet','change_requests'].includes(m),
          read:   ['attendance','leave','payroll','holidays','performance',
                   'overtime','timesheet','policies','change_requests'].includes(m),
          update: false,
          delete: false,
        }]));
      default:
        return Object.fromEntries(mods.map(m => [m, none(m)]));
    }
  }

  readonly roleColors: Record<string, string> = {
    super_admin: '#8b1a1a',
    admin:       '#8b1a1a',
    hr_admin:    '#c97d10',
    hr:          '#c97d10',
    manager:     '#166534',
    employee:    '#1565C0',
  };

  readonly roleIcons: Record<string, string> = {
    super_admin: 'shield',
    admin:       'admin_panel_settings',
    hr_admin:    'manage_accounts',
    hr:          'people',
    manager:     'groups',
    employee:    'person',
  };

  constructor(
    private apiService: ApiService,
    private snackbar: SnackbarService,
    private headerService: HeaderService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    if (this.mode !== 'both') this.activeTab = this.mode;
    if (this.mode === 'both') this.headerService.setTitle('Users & Roles');
    this.loadRoles();
    this.loadUsers();
    this.loadTabRegistry();
  }

  switchTab(tab: 'roles' | 'users'): void {
    this.activeTab = tab;
  }

  get activeRoleCount(): number { return this.roles.filter(r => r.status === 'active').length; }
  get systemRoleCount(): number { return this.roles.filter(r => r.isSystem).length; }
  get adminUsersCount(): number { return this.users.filter(u => ['admin','super_admin','hr_admin'].includes(u.role)).length; }
  get activeUsersCount(): number { return this.users.filter(u => this.isUserActive(u)).length; }
  get inactiveUsersCount(): number { return this.users.filter(u => !this.isUserActive(u)).length; }
  get managerUsersCount(): number { return this.users.filter(u => u.role === 'manager').length; }
  get hrUsersCount(): number { return this.users.filter(u => u.role === 'hr').length; }
  get employeeUsersCount(): number { return this.users.filter(u => u.role === 'employee').length; }

  get pagedUsers(): any[] {
    const start = (this.usersPage - 1) * this.usersPageSize;
    return this.filteredUsers.slice(start, start + this.usersPageSize);
  }

  loadRoles(): void {
    this.loading = true;
    this.apiService.get('/roles').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.roles = Array.isArray(res) ? res : (res?.data ?? []);
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  getRoleColor(role: any): string {
    return this.roleColors[role.name] ?? '#546E7A';
  }

  getRoleIcon(role: any): string {
    return this.roleIcons[role.name] ?? 'person';
  }

  getMenuCount(role: any): number {
    try {
      const m = typeof role.menuPermissions === 'string'
        ? JSON.parse(role.menuPermissions) : role.menuPermissions;
      return Array.isArray(m) ? m.length : 0;
    } catch { return 0; }
  }

  getModuleCount(role: any): number {
    try {
      const o = typeof role.operationPermissions === 'string'
        ? JSON.parse(role.operationPermissions) : role.operationPermissions;
      return o ? Object.keys(o).length : 0;
    } catch { return 0; }
  }

  openCreate(): void {
    this.isEdit = false;
    this.drawerMode = 'edit';
    this.roleForm = this.emptyForm();
    this.roleForm.opPerms = this.buildOpPermsModel(this.getOpDefaults('none'));
    this.seedExpanded([]);
    this.seedActiveSection([]);
    this.showDialog = true;
  }

  openEdit(role: any, event: Event): void {
    event.stopPropagation();
    this.isEdit = true;
    this.drawerMode = 'edit';
    let menuKeys: string[] = [];
    let tabKeys: string[] = [];
    let opPerms: any = {};
    try { menuKeys = JSON.parse(role.menuPermissions || '[]'); } catch {}
    try { tabKeys = JSON.parse(role.tabPermissions || '[]'); } catch {}
    try { opPerms = JSON.parse(role.operationPermissions || '{}'); } catch {}

    const hasPerms = opPerms && Object.keys(opPerms).length > 0;
    if (!hasPerms) {
      const presetKey = this.presets[role.name] ?? 'employee';
      opPerms = this.getOpDefaults(presetKey);
    }

    this.roleForm = {
      id: role.id,
      name: role.name,
      displayName: role.displayName || '',
      description: role.description || '',
      status: role.status ?? 'active',
      menuKeys,
      tabKeys,
      opPerms: this.buildOpPermsModel(opPerms),
    };
    this.seedExpanded(menuKeys);
    this.seedActiveSection(menuKeys);
    this.showDialog = true;
  }

  applyPreset(presetKey: string): void {
    this.roleForm.opPerms = this.buildOpPermsModel(this.getOpDefaults(presetKey));
  }

  closeDialog(): void {
    this.showDialog = false;
    if (this.drawerMode === 'view') this.selectedRole = null;
  }

  submitRole(): void {
    if (!this.roleForm.name) return;
    this.submitting = true;

    const payload = {
      name: this.roleForm.name,
      displayName: this.roleForm.displayName,
      description: this.roleForm.description,
      status: this.roleForm.status,
      menuPermissions: JSON.stringify(this.roleForm.menuKeys),
      tabPermissions: JSON.stringify(this.roleForm.tabKeys ?? []),
      operationPermissions: JSON.stringify(this.buildOpPermsJson()),
    };

    const req = this.isEdit
      ? this.apiService.put(`/roles/${this.roleForm.id}`, payload)
      : this.apiService.post('/roles', payload);

    req.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snackbar.showSuccess(this.isEdit ? 'Role updated' : 'Role created');
        this.showDialog = false;
        this.selectedRole = null;
        this.loadRoles();
        this.submitting = false;
      },
      error: (err: any) => {
        this.snackbar.showFailure(err?.error?.message || 'Failed to save role');
        this.submitting = false;
      }
    });
  }

  deleteRole(role: any, event: Event): void {
    event.stopPropagation();

    const assignedUsers = this.users.filter(u => u.role === role.name);
    if (assignedUsers.length > 0) {
      this.snackbar.showFailure(
        `Cannot delete — ${assignedUsers.length} user${assignedUsers.length > 1 ? 's are' : ' is'} assigned to "${role.displayName || role.name}". Reassign them first in the Users tab.`
      );
      return;
    }

    this.confirmDialog.confirm(
      `"${role.displayName || role.name}" will be permanently deleted. This cannot be undone.`,
      { title: 'Delete Role', confirmLabel: 'Delete', danger: true }
    ).then(ok => {
      if (!ok) return;
      this.apiService.delete(`/roles/${role.id}`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.snackbar.showSuccess('Role deleted');
          if (this.selectedRole?.id === role.id) this.selectedRole = null;
          this.loadRoles();
        },
        error: (err: any) => this.snackbar.showFailure(err?.error?.message || 'Delete failed')
      });
    });
  }

  selectRole(role: any): void {
    this.selectedRole = role;
    this.drawerMode = 'view';
    let grantedMenuKeys: string[] = [];
    try { grantedMenuKeys = JSON.parse(role.menuPermissions || '[]'); } catch {}
    this.seedExpanded(grantedMenuKeys);
    this.seedActiveSection(grantedMenuKeys);
    this.showDialog = true;
  }

  isMenuSelected(key: string): boolean {
    return this.roleForm.menuKeys?.includes(key) ?? false;
  }

  toggleMenu(key: string): void {
    const idx = this.roleForm.menuKeys.indexOf(key);
    if (idx > -1) this.roleForm.menuKeys.splice(idx, 1);
    else this.roleForm.menuKeys.push(key);
  }

  toggleAllMenus(): void {
    if (this.roleForm.menuKeys.length === this.menuItems.length) {
      this.roleForm.menuKeys = [];
    } else {
      this.roleForm.menuKeys = this.menuItems.map(m => m.key);
    }
  }

  allMenusSelected(): boolean {
    return this.roleForm.menuKeys?.length === this.menuItems.length;
  }

  isOpSelected(module: string, op: string): boolean {
    return this.roleForm.opPerms?.[module]?.[op] ?? false;
  }

  toggleOp(module: string, op: string): void {
    if (!this.roleForm.opPerms[module]) {
      this.roleForm.opPerms[module] = { create: false, read: false, update: false, delete: false };
    }
    this.roleForm.opPerms[module][op] = !this.roleForm.opPerms[module][op];
  }

  toggleAllOpsInModule(module: string): void {
    const all = this.ops.every(op => this.isOpSelected(module, op));
    if (!this.roleForm.opPerms[module]) {
      this.roleForm.opPerms[module] = { create: false, read: false, update: false, delete: false };
    }
    this.ops.forEach(op => this.roleForm.opPerms[module][op] = !all);
  }

  isModuleFullySelected(module: string): boolean {
    return this.ops.every(op => this.isOpSelected(module, op));
  }

  getSelectedMenuKeys(): string[] {
    if (!this.selectedRole) return [];
    try { return JSON.parse(this.selectedRole.menuPermissions || '[]'); } catch { return []; }
  }

  getSelectedOpModules(): string[] {
    if (!this.selectedRole) return [];
    try {
      const o = JSON.parse(this.selectedRole.operationPermissions || '{}');
      return Object.keys(o);
    } catch { return []; }
  }

  getOpSummary(module: string): string {
    try {
      const o = JSON.parse(this.selectedRole.operationPermissions || '{}');
      const m = o[module];
      if (!m) return '';
      return Object.entries(m).filter(([, v]) => v).map(([k]) => k).join(', ');
    } catch { return ''; }
  }

  getSelectedTabKeys(): string[] {
    if (!this.selectedRole) return [];
    try { return JSON.parse((this.selectedRole as any).tabPermissions || '[]'); } catch { return []; }
  }

  viewTabCount(pageKey: string): number {
    const granted = this.getSelectedTabKeys();
    return this.allTabsForMenu(pageKey).filter(t => granted.includes(t.key)).length;
  }

  isActiveUser(u: any): boolean {
    const s = (u?.status || '').toLowerCase();
    return s !== 'inactive' && s !== 'exited' && s !== 'exit' && s !== 'resigned' && s !== 'terminated';
  }

  togglingStatusFor: string | null = null;
  isUserActive(u: any): boolean { return (u?.status || 'active').toLowerCase() !== 'inactive'; }

  toggleUserStatus(user: any): void {
    const newStatus = this.isUserActive(user) ? 'inactive' : 'active';
    this.togglingStatusFor = user.id;
    this.apiService.patch(`/employees/${user.id}`, { status: newStatus }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        user.status = newStatus;
        this.togglingStatusFor = null;
        this.snackbar.showSuccess(`${user.firstName || 'User'} ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      },
      error: (err: any) => {
        this.togglingStatusFor = null;
        this.snackbar.showFailure(err?.error?.message || 'Failed to update status');
      }
    });
  }

  loadUsers(): void {
    this.usersLoading = true;
    this.apiService.get('/employees').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const all = res?.data ?? res ?? [];
        this.users = all;
        this.usersLoading = false;
      },
      error: () => { this.usersLoading = false; }
    });
  }

  get filteredUsers(): any[] {
    let list = this.users;
    if (this.roleFilter) list = list.filter(u => u.role === this.roleFilter);
    if (this.userSearch) {
      const term = this.userSearch.toLowerCase();
      list = list.filter(u =>
        (u.firstName || '').toLowerCase().includes(term) ||
        (u.lastName  || '').toLowerCase().includes(term) ||
        (u.email     || '').toLowerCase().includes(term) ||
        (u.employeeId|| '').toLowerCase().includes(term)
      );
    }
    return list;
  }

  onUserSearchChange(): void { this.usersPage = 1; }
  onRoleFilterChange(): void { this.usersPage = 1; }

  assignRole(user: any, newRole: string): void {
    if (!newRole || user.role === newRole) return;
    this.changingRoleFor = user.id;
    this.apiService.patch(`/employees/${user.id}`, { role: newRole }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        user.role = newRole;
        this.changingRoleFor = null;
        this.snackbar.showSuccess(`${user.firstName}'s role updated to ${newRole}`);
      },
      error: (err: any) => {
        this.changingRoleFor = null;
        this.snackbar.showFailure(err?.error?.message || 'Failed to update role');
      }
    });
  }

  get roleOptions() { return (this.roles || []).map((r: any) => ({ label: r.displayName || this.prettyRole(r.name), value: r.name })); }

  get roleFilterOptions() { return [{ label: 'All Roles', value: '' }, ...this.roleOptions]; }

  prettyRole(name: string): string {
    if (!name) return '';
    const acronyms = new Set(['hr', 'ceo', 'cto', 'coo', 'cfo', 'it']);
    return name.split(/[_\s]+/).map(w =>
      acronyms.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    ).join(' ');
  }
  sessionOptions = [
    { label: 'Default', value: '' },
    { label: '3 min', value: 3 }, { label: '5 min', value: 5 }, { label: '10 min', value: 10 },
    { label: '15 min', value: 15 }, { label: '30 min', value: 30 }, { label: '60 min', value: 60 }, { label: '120 min', value: 120 },
  ];

  savingSessionFor: string | null = null;
  setSessionTimeout(user: any, value: string): void {
    const mins = value === '' || value == null ? null : Number(value);
    this.savingSessionFor = user.id;
    this.apiService.patch(`/employees/${user.id}`, { sessionTimeoutMinutes: mins }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        user.sessionTimeoutMinutes = mins;
        this.savingSessionFor = null;
        this.snackbar.showSuccess(`${user.firstName}'s session ${mins ? 'set to ' + mins + ' min' : 'reset to default'}`);
      },
      error: (err: any) => {
        this.savingSessionFor = null;
        this.snackbar.showFailure(err?.error?.message || 'Failed to update session timeout');
      }
    });
  }

  getUserInitials(user: any): string {
    return ((user.firstName?.[0] ?? '') + (user.lastName?.[0] ?? '')).toUpperCase() || '?';
  }

  credStatus(user: any): { key: string; label: string; icon: string; detail: string } {
    const shared = !!user?.credentialsShared || !!user?.credentialsSharedAt;
    const pending = !!user?.mustChangePassword;
    if (!shared) {
      return { key: 'not-shared', label: 'Not Shared', icon: 'mark_email_unread', detail: 'Login credentials not sent yet' };
    }
    if (pending) {
      return { key: 'awaiting', label: 'Awaiting Setup', icon: 'schedule', detail: this.sharedAgo(user) };
    }
    return { key: 'active', label: 'Active', icon: 'check_circle', detail: this.sharedAgo(user) };
  }

  sharedAgo(user: any): string {
    const raw = user?.credentialsSharedAt;
    if (!raw) return 'Credentials shared';
    const then = new Date(raw).getTime();
    if (isNaN(then)) return 'Credentials shared';
    const mins = Math.floor((Date.now() - then) / 60000);
    if (mins < 1) return 'Shared just now';
    if (mins < 60) return `Shared ${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Shared ${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `Shared ${days}d ago`;
    return 'Shared ' + new Date(raw).toLocaleDateString();
  }

  sharingCredsFor: string | null = null;
  shareCredentials(user: any): void {
    if (!user?.id) return;
    this.confirmDialog.confirm(
      `This will generate login credentials for ${user.firstName || 'this user'} and email them to ${user.email || 'their email'}.`,
      { title: 'Share Credentials', confirmLabel: 'Send', danger: false }
    ).then(ok => {
      if (!ok) return;
      this.sharingCredsFor = user.id;
      this.apiService.post(`/employees/${user.id}/share-credentials`, {}).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          user.credentialsShared = true;
          user.credentialsSharedAt = new Date().toISOString();
          this.sharingCredsFor = null;
          this.snackbar.showSuccess(`Credentials emailed to ${user.email || user.firstName}`);
        },
        error: (err: any) => {
          this.sharingCredsFor = null;
          this.snackbar.showFailure(err?.error?.message || 'Failed to share credentials');
        }
      });
    });
  }

  showPwdDialog = false;
  pwdUser: any = null;
  pwdSubmitting = false;
  showPwdText = false;
  pwdForm = { newPassword: '', confirmPassword: '' };

  openChangePassword(user: any): void {
    this.pwdUser = user;
    this.pwdForm = { newPassword: '', confirmPassword: '' };
    this.showPwdText = false;
    this.showPwdDialog = true;
  }

  closeChangePassword(): void {
    this.showPwdDialog = false;
    this.pwdUser = null;
  }

  generatePassword(): void {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnpqrstuvwxyz';
    const digits = '23456789';
    const special = '@#$%&*!';
    const all = upper + lower + digits + special;
    const pick = (set: string) => set[Math.floor(Math.random() * set.length)];
    let pwd = pick(upper) + pick(lower) + pick(digits) + pick(special);
    for (let i = 0; i < 8; i++) pwd += pick(all);
    pwd = pwd.split('').sort(() => Math.random() - 0.5).join('');
    this.pwdForm = { newPassword: pwd, confirmPassword: pwd };
    this.showPwdText = true;
  }

  submitChangePassword(): void {
    if (this.pwdForm.newPassword.length < 8) { this.snackbar.showFailure('Password must be at least 8 characters'); return; }
    if (this.pwdForm.newPassword !== this.pwdForm.confirmPassword) { this.snackbar.showFailure('Passwords do not match'); return; }
    this.pwdSubmitting = true;
    this.apiService.post(`/employees/${this.pwdUser.id}/reset-password`, {
      newPassword: this.pwdForm.newPassword
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.pwdSubmitting = false;
        this.showPwdDialog = false;
        if (this.pwdUser) this.pwdUser.mustChangePassword = true;
        this.snackbar.showSuccess(`Password updated for ${this.pwdUser?.firstName || 'user'}`);
        this.pwdUser = null;
      },
      error: (err: any) => {
        this.pwdSubmitting = false;
        this.snackbar.showFailure(err?.error?.message || 'Failed to change password');
      }
    });
  }

  private emptyForm(): any {
    return {
      id: null,
      name: '',
      displayName: '',
      description: '',
      status: 'active',
      menuKeys: [],
      tabKeys: [],
      opPerms: {},
    };
  }

  private buildOpPermsModel(existing: any): any {
    const model: any = {};
    this.modules.forEach(mod => {
      model[mod] = {
        create: existing[mod]?.create ?? false,
        read:   existing[mod]?.read   ?? false,
        update: existing[mod]?.update ?? false,
        delete: existing[mod]?.delete ?? false,
      };
    });
    return model;
  }

  private buildOpPermsJson(): any {
    const result: any = {};
    this.modules.forEach(mod => {
      const m = this.roleForm.opPerms[mod];
      if (m && (m.create || m.read || m.update || m.delete)) {
        result[mod] = m;
      }
    });
    return result;
  }
}
